import { categoryLabel, displayedPrice, inventoryCurrency as currency, itemDimensions, itemModel } from "./inventory-format.js";
import { normalizedPhotos, selectedCoverId } from "./inventory-photos.js";

if (location.hash.startsWith("#invite_token=") || location.hash.startsWith("#recovery_token=")) location.replace(`/inventory-admin/${location.hash}`);

const grids = document.querySelectorAll(".inventory-grid[data-inventory-category]");
const detailsDialog = document.querySelector("[data-inventory-details-dialog]");
const quoteDialog = document.querySelector("[data-inventory-quote-dialog]");
const quoteForm = quoteDialog?.querySelector("form");

function text(tag, value, className) {
  const element = document.createElement(tag);
  element.textContent = value;
  if (className) element.className = className;
  return element;
}

function optimizedImage(source, width) {
  return source ? `/.netlify/images?url=${encodeURIComponent(source)}&w=${width}&q=78` : "";
}

function makeImage(item, { eager = false } = {}) {
  if (!item.image) {
    const placeholder = text("div", "Photo not yet available", "inventory-image-placeholder");
    placeholder.setAttribute("role", "img");
    placeholder.setAttribute("aria-label", `Photo not available for ${item.title || "this building"}`);
    return placeholder;
  }
  const image = document.createElement("img");
  image.className = "product-img inventory-img";
  image.src = optimizedImage(item.image, 480);
  image.srcset = [320, 480, 720].map((width) => `${optimizedImage(item.image, width)} ${width}w`).join(", ");
  image.sizes = "(max-width: 880px) 280px, 320px";
  image.alt = `${item.title || "Building"} at Middle Tennessee Portable Buildings in Lewisburg, Tennessee`;
  image.width = 640;
  image.height = 480;
  image.loading = eager ? "eager" : "lazy";
  image.decoding = "async";
  if (eager) image.fetchPriority = "high";
  return image;
}

function makeGallery(item) {
  const photos = normalizedPhotos(item);
  if (photos.length === 0) return makeImage(item, { eager: true });
  const gallery = document.createElement("section");
  gallery.className = "inventory-gallery";
  gallery.setAttribute("aria-label", `${item.title || "Building"} photos`);
  const track = document.createElement("div");
  track.className = "inventory-gallery__track";
  track.tabIndex = 0;
  const coverId = selectedCoverId(item, photos);
  const ordered = [...photos].sort((a, b) => Number(b.id === coverId) - Number(a.id === coverId));
  ordered.forEach((photo, index) => {
    const slide = document.createElement("div");
    slide.className = "inventory-gallery__slide";
    const image = document.createElement("img");
    image.src = optimizedImage(photo.url, 960);
    image.srcset = [480, 720, 960, 1280].map((width) => `${optimizedImage(photo.url, width)} ${width}w`).join(", ");
    image.sizes = "(max-width: 720px) calc(100vw - 56px), 630px";
    image.alt = `${item.title || "Building"}, photo ${index + 1} of ${ordered.length}`;
    image.width = photo.width || 960;
    image.height = photo.height || 720;
    image.loading = index === 0 ? "eager" : "lazy";
    image.decoding = "async";
    slide.append(image);
    track.append(slide);
  });
  gallery.append(track);
  if (ordered.length === 1) return gallery;

  const controls = document.createElement("div");
  controls.className = "inventory-gallery__controls";
  const previous = text("button", "Previous", "inventory-gallery__button");
  const next = text("button", "Next", "inventory-gallery__button");
  previous.type = next.type = "button";
  const counter = text("span", `1 of ${ordered.length}`, "inventory-gallery__counter");
  counter.setAttribute("aria-live", "polite");
  let active = 0;
  const update = (index, shouldScroll = true) => {
    active = Math.max(0, Math.min(ordered.length - 1, index));
    previous.disabled = active === 0;
    next.disabled = active === ordered.length - 1;
    counter.textContent = `${active + 1} of ${ordered.length}`;
    if (shouldScroll) track.children[active].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  };
  previous.addEventListener("click", () => update(active - 1));
  next.addEventListener("click", () => update(active + 1));
  track.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") { event.preventDefault(); update(active - 1); }
    if (event.key === "ArrowRight") { event.preventDefault(); update(active + 1); }
  });
  let scrollFrame;
  track.addEventListener("scroll", () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => update(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)), false));
  }, { passive: true });
  controls.append(previous, counter, next);
  gallery.append(controls);
  update(0, false);
  return gallery;
}

function appendPrice(container, item) {
  const priceValue = item.price === null || item.price === "" ? NaN : Number(item.price);
  const originalValue = item.originalPrice === null || item.originalPrice === "" ? NaN : Number(item.originalPrice);
  const price = text("p", "", "inventory-price");
  if (!Number.isFinite(priceValue)) price.append(text("b", "Call for current price"));
  else if (Number.isFinite(originalValue) && originalValue > priceValue) {
    price.append(text("span", `Cash price: ${currency.format(priceValue)}`, "sale-price"), " ");
    price.append(text("span", `Previously ${currency.format(originalValue)}`, "was-price"));
  } else price.append("Cash price: ", text("b", currency.format(priceValue)));
  container.append(price);

  const rtoValue = item.rto === null || item.rto === "" ? NaN : Number(item.rto);
  if (Number.isFinite(rtoValue)) {
    const rto = text("p", "Rent-to-own: ", "rto");
    rto.append(text("b", `${currency.format(rtoValue)}/mo`));
    container.append(rto);
  } else container.append(text("p", "Rent-to-own payment not listed.", "rto"));
}

function buildSummary(item) {
  const summary = document.createElement("dl");
  summary.className = "inventory-summary";
  for (const [label, value] of [
    ["Inventory ID", item.id || "Not listed"],
    ["Category", categoryLabel(item.category)],
    ["Dimensions", itemDimensions(item)],
    ["Model", itemModel(item)],
    ["Displayed price", displayedPrice(item)],
    ["Availability", item.status === "available" ? "Listed as available — please confirm" : "Call to confirm"],
  ]) summary.append(text("dt", label), text("dd", value));
  return summary;
}

function openQuote(item) {
  if (!quoteDialog || !quoteForm) return;
  const details = {
    inventory_id: item.id || "",
    product_category: item.category || "portable",
    dimensions: itemDimensions(item) === "Dimensions not listed" ? "" : itemDimensions(item),
    model: itemModel(item),
    displayed_price: displayedPrice(item),
    building_type: categoryLabel(item.category),
  };
  for (const [name, value] of Object.entries(details)) quoteForm.elements[name].value = value;
  quoteForm.action = `/quote-request-success?inventory_id=${encodeURIComponent(details.inventory_id)}&product_category=${encodeURIComponent(details.product_category)}`;
  quoteDialog.querySelector("[data-quote-selection]").replaceChildren(
    text("strong", item.title || "Selected building"),
    text("span", `${details.displayed_price} · ${details.inventory_id || "No inventory ID"}`),
  );
  quoteDialog.showModal();
  quoteForm.elements.name.focus();
}

function openDetails(item) {
  if (!detailsDialog) return;
  detailsDialog.querySelector("[data-details-body]").replaceChildren(
    makeGallery(item),
    text("h2", item.title || "Building details"),
    buildSummary(item),
    text("p", item.description || "Additional details are not listed. Call or request a quote with this inventory ID."),
  );
  detailsDialog.querySelector("[data-details-quote]").onclick = () => {
    detailsDialog.close();
    openQuote(item);
  };
  detailsDialog.showModal();
}

function renderItem(item) {
  const article = document.createElement("article");
  article.className = "card inventory-card";
  article.dataset.inventoryId = item.id || "";
  article.append(makeImage(item));
  const pad = document.createElement("div");
  pad.className = "pad";
  const meta = document.createElement("div");
  meta.className = "inventory-meta";
  meta.append(text("span", itemDimensions(item), "inventory-dimensions"), text("span", categoryLabel(item.category), "inventory-category"));
  pad.append(meta, text("h3", item.title || "Building details not listed"));
  appendPrice(pad, item);
  pad.append(text("p", item.description || "Description not yet available."));
  pad.append(text("p", item.status === "available" ? "Listed as available — call to confirm" : "Availability not listed — call to confirm", "inventory-availability"));
  const actions = document.createElement("div");
  actions.className = "inventory-card-actions";
  const details = text("button", "View Details", "btn light");
  details.type = "button";
  details.addEventListener("click", () => openDetails(item));
  const quote = text("button", "Request Quote", "btn primary");
  quote.type = "button";
  quote.addEventListener("click", () => openQuote(item));
  actions.append(details, quote);
  pad.append(actions);
  article.append(pad);
  return article;
}

async function refreshInventory() {
  await Promise.all([...grids].map(async (grid) => {
    const status = grid.dataset.inventoryStatus ? document.getElementById(grid.dataset.inventoryStatus) : null;
    const toggle = grid.dataset.inventoryToggle ? document.getElementById(grid.dataset.inventoryToggle) : null;
    const limit = Number.parseInt(grid.dataset.inventoryLimit ?? "", 10);
    const updateToggle = () => {
      if (!toggle || !Number.isFinite(limit)) return;
      const hasMore = grid.children.length > limit;
      toggle.hidden = !hasMore;
      if (!hasMore) {
        grid.classList.remove("show-all");
        toggle.setAttribute("aria-expanded", "false");
        toggle.textContent = "View all current inventory";
      }
    };
    if (toggle && Number.isFinite(limit)) {
      toggle.addEventListener("click", () => {
        const isExpanded = grid.classList.toggle("show-all");
        toggle.setAttribute("aria-expanded", String(isExpanded));
        toggle.textContent = isExpanded ? "Show featured inventory" : "View all current inventory";
        if (!isExpanded) grid.closest("section")?.scrollIntoView({ block: "start" });
      });
      updateToggle();
    }
    try {
      const category = grid.dataset.inventoryCategory;
      const response = await fetch(`/api/inventory?category=${encodeURIComponent(category)}`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Inventory request failed");
      const { items } = await response.json();
      if (!Array.isArray(items)) throw new Error("Inventory response was invalid");
      if (items.length === 0) {
        const empty = document.createElement("div");
        empty.className = "inventory-state";
        empty.append(text("h3", "No current listings are posted in this category."), text("p", "Inventory changes quickly. Call (931) 288-4576 to ask what is available or arriving soon."));
        grid.replaceChildren(empty);
        if (status) status.textContent = "No current listings are posted. Call for the latest availability.";
        updateToggle();
        return;
      }
      grid.replaceChildren(...items.map(renderItem));
      if (status) status.textContent = `Showing ${items.length} current ${items.length === 1 ? "listing" : "listings"}. Availability should be confirmed.`;
      updateToggle();
    } catch {
      if (status) {
        status.textContent = "Live inventory could not be refreshed. Showing recently listed buildings; call to confirm availability.";
        status.classList.add("is-warning");
      }
    } finally { grid.setAttribute("aria-busy", "false"); }
  }));
}

for (const dialog of [detailsDialog, quoteDialog]) {
  dialog?.querySelector("[data-dialog-close]")?.addEventListener("click", () => dialog.close());
  dialog?.addEventListener("click", (event) => { if (event.target === dialog) dialog.close(); });
}

refreshInventory();

const filterButtons = [...document.querySelectorAll("[data-inventory-filter]")];
const filterPanels = [...document.querySelectorAll("[data-inventory-panel]")];
function activateCategory(category, moveFocus = false) {
  filterButtons.forEach((button) => {
    const isActive = button.dataset.inventoryFilter === category;
    button.setAttribute("aria-selected", String(isActive));
    button.tabIndex = isActive ? 0 : -1;
    if (isActive && moveFocus) button.focus();
  });
  filterPanels.forEach((panel) => { panel.hidden = panel.dataset.inventoryPanel !== category; });
}
filterButtons.forEach((button, index) => {
  button.addEventListener("click", () => activateCategory(button.dataset.inventoryFilter));
  button.addEventListener("keydown", (event) => {
    let nextIndex = null;
    if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (index + 1) % filterButtons.length;
    if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (index - 1 + filterButtons.length) % filterButtons.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = filterButtons.length - 1;
    if (nextIndex == null) return;
    event.preventDefault();
    activateCategory(filterButtons[nextIndex].dataset.inventoryFilter, true);
  });
});
